import { fixFloat } from '@/helpers'
import { normalizeOrderItemExtras } from '@/utils/Extra'
import { getOrderItemValue } from '@/utils/Company/Order/Item'

/**
 * Normalize the products of an order item.
 *
 * @param {Object} orderItem - The order item to normalize
 * @return {Array} The normalized order item products
 */
export function normalizeOrderItemProducts(orderItem) {
  return (orderItem.products ?? []).map(orderItemProduct =>
    normalizeOrderItemProduct({ orderItem, orderItemProduct }),
  )
}

/**
 * Normalize the order item product by combining extras from product and category,
 * normalizing extras, calculating total price with extras, and defining unit price.
 *
 * @param {Object} orderItem - The order item object
 * @param {Object} orderItemProduct - The order item product object
 * @return {Object} The normalized order item product object
 */
export function normalizeOrderItemProduct({ orderItem, orderItemProduct }) {
  let { extras = [] } = orderItemProduct

  if (orderItemProduct.originalExtras === undefined) {
    const productExtras = orderItemProduct.extras ?? []
    const categoryExtras = orderItemProduct.category?.extras ?? []

    extras = [...productExtras, ...categoryExtras]

    orderItemProduct.originalExtras = productExtras
  }

  extras = normalizeOrderItemExtras({ orderItem, extras })

  orderItemProduct = {
    ingredients: [],
    price: getOrderItemValue({ orderItem, value: orderItemProduct.price }),
    ...orderItemProduct,
    extras,
    get removedIngredients() {
      return this.ingredients.filter(({ removed }) => removed)
    },
    get selectedExtras() {
      const { extras } = this

      return extras.filter(({ selectedItems }) => selectedItems.length > 0)
    },

    get extrasTotalPrice() {
      const { selectedExtras } = this

      let extrasTotalPrice = selectedExtras.reduce(
        (acc, { totalPrice }) => acc + totalPrice,
        0,
      )

      extrasTotalPrice = fixFloat(extrasTotalPrice)

      return extrasTotalPrice
    },
  }

  if (!('unitPrice' in orderItemProduct))
    Object.defineProperty(orderItemProduct, 'unitPrice', {
      get: function () {
        const { price, extrasTotalPrice } = this

        let unitPrice = price + extrasTotalPrice

        unitPrice = fixFloat(unitPrice)

        return unitPrice
      },
    })

  return orderItemProduct
}
